import dayjs from 'dayjs'
import gql from 'graphql-tag'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import set from 'lodash/set'
import React, { useCallback, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import Event from '../../api/events/model'
import { useQuery } from '../../api/graphql'
import CommunitiesContextProvider from '../../hooks/CommunitiesContextProvider'
import useEventFormData from '../../hooks/useEventFormData'
import { CurrentUserContext } from '../../providers/CurrentUserContext'
import EventNew from '../components/events/EventNew'

// Fields copied from the source event when duplicating. Excludes any
// "When" fields (start/end, allDay, eventType, repeats*, occurrences) so
// the user is forced to pick a fresh schedule and choose between a single
// occurrence and an event series.
export const EventForDuplicationQuery = gql`
  query EventForDuplicationQuery($id: String!) {
    eventForDuplication(id: $id) {
      id
      title
      description
      category
      subcategory
      tags
      ageRanges
      dressCode
      costTier
      costMin
      costMax
      image_publicId: imagePublicId
      image_width: imageWidth
      image_height: imageHeight
      useTempPlace
      submitterOwnsEvent
      isVirtual
      url
      needTicketOrRsvp
      ticketOrRsvpUrl

      place {
        id
        slug
        name
      }

      organizer_email: organizerEmail
      organizer_name: organizerName
      organizer_phone: organizerPhone

      submittedOnBehalfOfOrg {
        id
        name
        contactEmail
        phone
      }

      tempPlace_name: tempPlaceName
      tempPlace_url: tempPlaceUrl
      tempPlace_address_city: tempPlaceAddressCity
      tempPlace_address_state: tempPlaceAddressState
      tempPlace_address_streetAddress: tempPlaceAddressStreetAddress
      tempPlace_address_streetAddress2: tempPlaceAddressStreetAddress2
      tempPlace_address_zipCode: tempPlaceAddressZipCode
    }
  }
`

const EventNewContainer: React.FC = () => {
  const { currentUser, loading: loadingCurrentUser } = useContext(CurrentUserContext)
  const { data, loading } = useEventFormData()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const contentFeedId = searchParams.get('content_feed_id')
  const duplicateFromId = searchParams.get('duplicateFromId')

  const { data: duplicateData, loading: loadingDuplicate } = useQuery(EventForDuplicationQuery, {
    variables: { id: duplicateFromId },
    skip: !duplicateFromId,
  })
  const sourceEvent = duplicateData?.eventForDuplication

  const initEvent = useCallback(() => {
    const event = new Event({
      eventType: null,
      repeatStopsOn: dayjs().add(5, 'day').toDate(),
      organizerName: currentUser?.fullName,
      organizerEmail: currentUser?.email,
      organizer: {
        name: currentUser?.fullName,
        email: currentUser?.email,
      },
      submitterOwnsEvent: true,
      primaryCommunityId: currentUser?.primaryCommunity?.id,
      ...(contentFeedId ? { submittedViaContentFeedId: contentFeedId } : {}),
    })
    // initial start date and time are rounded UP to the nearest hour,
    // 1 day from now
    const tomorrowNextHour = dayjs().startOf('hour').add(1, 'hour').add(1, 'day')
    event.startAt = tomorrowNextHour.toDate()
    event.endAt = tomorrowNextHour.add(3, 'hour').toDate()
    return event
  }, [currentUser, contentFeedId])

  const event = useMemo(() => {
    const blank = initEvent()
    if (!sourceEvent) return blank

    // sourceEvent has already been transformed by useQuery's convertResponse
    // (e.g. image_publicId → image.publicId, organizer_email → organizer.email,
    // tempPlace_name → tempPlace.name). Read from the nested shape, not the
    // flat aliased keys. Date / recurrence fields are deliberately left at
    // the new-event defaults so the user must re-pick the entire When section.
    blank.title = sourceEvent.title
    blank.description = sourceEvent.description
    blank.category = sourceEvent.category
    blank.subcategory = sourceEvent.subcategory
    blank.tags = sourceEvent.tags
    blank.ageRanges = sourceEvent.ageRanges
    blank.dressCode = sourceEvent.dressCode
    blank.costTier = sourceEvent.costTier
    blank.costMin = sourceEvent.costMin
    blank.costMax = sourceEvent.costMax
    blank.isVirtual = sourceEvent.isVirtual
    blank.url = sourceEvent.url
    blank.needTicketOrRsvp = sourceEvent.needTicketOrRsvp
    blank.ticketOrRsvpUrl = sourceEvent.ticketOrRsvpUrl
    blank.useTempPlace = sourceEvent.useTempPlace
    blank.submitterOwnsEvent = sourceEvent.submitterOwnsEvent

    if (sourceEvent.image?.publicId) {
      // The copy points at the same stored file, so its size is the same one.
      // Copying it means a duplicated event's page holds its image's box open
      // from the first render instead of waiting on the backfill.
      blank.image = {
        publicId: sourceEvent.image.publicId,
        width: sourceEvent.image.width,
        height: sourceEvent.image.height,
      }
    }

    if (sourceEvent.place) {
      blank.place = sourceEvent.place
      blank.placeId = sourceEvent.place.id
    }

    if (sourceEvent.tempPlace) {
      blank.tempPlace = omitBy(sourceEvent.tempPlace, isNil)
      if (sourceEvent.tempPlace.address) {
        set(blank, 'tempPlace.address', omitBy(sourceEvent.tempPlace.address, isNil))
      }
    }

    if (sourceEvent.organizer) {
      blank.organizer = omitBy(sourceEvent.organizer, isNil)
    }

    if (sourceEvent.submittedOnBehalfOfOrg) {
      blank.submittedOnBehalfOfOrg = sourceEvent.submittedOnBehalfOfOrg
    }

    return blank
  }, [initEvent, sourceEvent])

  return (
    <CommunitiesContextProvider value={data?.communities}>
      <EventNew loading={loading || loadingCurrentUser || loadingDuplicate} event={event} />
    </CommunitiesContextProvider>
  )
}

export default EventNewContainer
